/**
 * ⌘K buyruq paneli (redesign v2): bitta joydan qidirish va o'tish.
 * - bo'sh so'rov: amallar (driver qo'shish, vakansiya, eslatma), oxirgi driverlar, bo'limlar;
 * - matn (2+ belgi): umumiy qidiruv bitta so'rovda (`GET /api/search/`, 250 ms kechikish) -- driverlar doim birinchi,
 *   keyin xodimlar, carrier'lar, vakansiyalar, so'ngra mos amallar va bo'limlar (tartib: features/search/palette.ts);
 *   bo'lim to'liq chiqmasa "Barchasini ko'rish (N)" ro'yxat sahifasini shu so'rov bilan ochadi;
 * - klaviatura: ↑↓, Enter, Esc; ⌘K / Ctrl+K ochadi-yopadi (AppShell).
 * Owner uchun "Driver qo'shish" aynan shu yerda: bosh sahifada yo'q (qaror 2026-10-17).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Bell, Briefcase, Clock3, Plus, Search, Truck, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { can, type Me } from "@/features/auth/types";
import { useRecentDrivers } from "@/features/drivers/recent";
import { MIN_QUERY, useGlobalSearch } from "@/features/search/api";
import { hiddenCount, listLink, matches, sectionOrder, type Section } from "@/features/search/palette";
import { goToItems } from "@/app/nav";
import { StatusPill } from "@/components/ui/badge";

interface Row {
  key: string;
  section: Section;
  label: string;
  meta?: string;
  icon?: LucideIcon;
  initials?: string;
  status?: string;
  /** "Barchasini ko'rish" qatori: ro'yxat sahifasiga havola, matn yumshoqroq */
  more?: boolean;
  /** Aniq emas, o'xshash natija (xato yozilgan so'rov) */
  similar?: boolean;
  run: () => void;
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

export function CommandPalette({ me, open, onClose }: { me: Me; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const recent = useRecentDrivers(me?.id);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setDebounced("");
    setIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 10);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(id);
  }, [query]);

  const search = useGlobalSearch(debounced, open);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const rows = useMemo<Row[]>(() => {
    const q = query.trim();
    const hasQuery = q.length >= MIN_QUERY;
    const actions: Row[] = [];
    actions.push({ key: "add-driver", section: "actions", label: t("palette.addDriver"), meta: t("palette.addDriverHint"), icon: Plus, run: () => go("/drivers/new") });
    if (can(me, "carriers_edit")) actions.push({ key: "new-job", section: "actions", label: t("palette.newJob"), icon: Briefcase, run: () => go("/jobs?new=1") });
    actions.push({ key: "new-reminder", section: "actions", label: t("palette.newReminder"), icon: Bell, run: () => go("/tasks?new=1") });
    const bySection: Record<Section, Row[]> = {
      actions: actions.filter((a) => matches(q, a.label)),
      recent: [],
      drivers: [],
      people: [],
      carriers: [],
      jobs: [],
      goTo: goToItems(me)
        .map((item) => ({ key: `go-${item.label}`, section: "goTo" as const, label: t(`nav.${item.label}`), icon: item.icon, run: () => go(item.to) }))
        .filter((a) => matches(q, a.label)),
    };
    const more = (section: "drivers" | "carriers" | "jobs", count: number, shown: number): Row[] =>
      hiddenCount(count, shown) > 0
        ? [{ key: `more-${section}`, section, label: t("palette.showAll", { count }), icon: Search, more: true, run: () => go(listLink(section, q)) }]
        : [];
    const r = hasQuery ? search.data : undefined;
    if (r) {
      bySection.drivers = [
        ...r.drivers.items.map((d) => ({
          key: `d-${d.id}`,
          section: "drivers" as const,
          label: d.full_name,
          meta: [d.state, d.phone_display].filter(Boolean).join(" · "),
          initials: initialsOf(d.full_name),
          status: d.status,
          similar: d.similar,
          run: () => go(`/drivers/${d.id}`),
        })),
        ...more("drivers", r.drivers.count, r.drivers.items.length),
      ];
      bySection.people = r.people.items.map((p) => ({
        key: `p-${p.id}`,
        section: "people" as const,
        label: p.full_name,
        meta: [t(`roles.${p.role}`), p.branch].filter(Boolean).join(" · "),
        initials: initialsOf(p.full_name),
        status: p.is_blocked ? "blocked" : undefined,
        similar: p.similar,
        run: () => go(`/people/${p.id}`),
      }));
      bySection.carriers = [
        ...r.carriers.items.map((c) => ({
          key: `c-${c.id}`,
          section: "carriers" as const,
          label: c.name,
          meta: [c.mc_number, c.state].filter(Boolean).join(" · "),
          icon: Truck,
          status: c.status,
          similar: c.similar,
          run: () => go(`/carriers/${c.id}`),
        })),
        ...more("carriers", r.carriers.count, r.carriers.items.length),
      ];
      bySection.jobs = [
        ...r.jobs.items.map((j) => ({
          key: `j-${j.id}`,
          section: "jobs" as const,
          label: j.title,
          meta: j.carrier_name,
          icon: Briefcase,
          status: j.status,
          similar: j.similar,
          run: () => go(`/jobs/${j.id}`),
        })),
        ...more("jobs", r.jobs.count, r.jobs.items.length),
      ];
    } else if (!hasQuery) {
      bySection.recent = recent
        .filter((d) => matches(q, d.name))
        .map((d) => ({ key: `r-${d.id}`, section: "recent" as const, label: d.name, meta: d.meta, initials: initialsOf(d.name), status: d.status, run: () => go(`/drivers/${d.id}`) }));
    }
    return sectionOrder(hasQuery).flatMap((s) => bySection[s]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, search.data, recent, me, t]);

  useEffect(() => setIndex(0), [rows.length, debounced]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [index, open]);

  if (!open) return null;

  const titles: Record<Section, string> = {
    actions: t("palette.actions"),
    recent: t("palette.recent"),
    drivers: t("nav.drivers"),
    people: t("nav.people"),
    carriers: t("nav.carriers"),
    jobs: t("nav.jobs"),
    goTo: t("palette.goTo"),
  };
  const hasQuery = query.trim().length >= MIN_QUERY;
  let runningIndex = -1;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      rows[index]?.run();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div className="fade-in fixed inset-0 z-[60] bg-black/30 backdrop-blur-[2px] dark:bg-black/60" onMouseDown={onClose} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("palette.searchButton")}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className="pop-in-center absolute top-[10vh] left-1/2 w-[640px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-card border border-border/60 bg-surface shadow-menu"
      >
        <label className="flex h-14 items-center gap-3 border-b border-line px-5 text-muted">
          <Search size={18} aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("palette.placeholder")}
            className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-muted"
            aria-label={t("palette.placeholder")}
            autoComplete="off"
          />
          <kbd className="rounded-md border border-border px-1.5 py-0.5 text-[10.5px] font-semibold text-muted">esc</kbd>
        </label>
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto overscroll-contain p-2" aria-busy={hasQuery && search.isFetching}>
          {rows.length === 0 && (
            <div className="px-3 py-8 text-center text-[13px] text-muted">{hasQuery && (search.isPending || search.isFetching) ? t("palette.searching") : t("palette.noResults")}</div>
          )}
          {sectionOrder(hasQuery).map((s) => {
            const items = rows.filter((r) => r.section === s);
            if (items.length === 0) return null;
            return (
              <div key={s}>
                <div className="px-3 pt-2.5 pb-1 text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{titles[s]}</div>
                {items.map((row) => {
                  runningIndex += 1;
                  const i = runningIndex;
                  const Icon = row.icon;
                  return (
                    <button
                      key={row.key}
                      type="button"
                      data-index={i}
                      onMouseEnter={() => setIndex(i)}
                      onClick={row.run}
                      className={cn("flex h-11 w-full items-center gap-3 rounded-xl px-3 text-left transition-colors", i === index ? "bg-fill" : "hover:bg-fill/60")}
                    >
                      <span
                        className={cn(
                          "grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold",
                          i === index && row.section === "actions" ? "bg-accent text-on-accent" : "bg-fill text-ink",
                          row.more && "bg-transparent text-muted",
                        )}
                      >
                        {Icon ? <Icon size={14} strokeWidth={2} aria-hidden /> : row.initials}
                      </span>
                      <span className={cn("min-w-0 flex-1 truncate text-[14px]", row.more ? "text-ink-soft" : "font-medium")}>
                        {row.label}
                        {row.meta && <span className="ml-2 font-normal text-muted">{row.meta}</span>}
                        {row.similar && <span className="ml-2 rounded-full bg-fill px-1.5 py-px text-[10.5px] font-normal text-muted">{t("palette.similar")}</span>}
                      </span>
                      {row.status && <StatusPill status={row.status} />}
                      {row.section === "recent" && <Clock3 size={13} className="text-faint" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="border-t border-line px-5 py-2 text-[11px] text-muted">{t("palette.hint")}</div>
      </div>
    </div>
  );
}

export type { ReactNode };
